import { Component, TemplateRef, ViewChild, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppService } from 'src/app/app.service';
import { HttpEventType } from '@angular/common/http';
import { messageService } from 'src/app/message.service';
import { OneQmsFileUploadService } from '../one-qms-file-upload.service';
import { SuccessSnackbarComponent } from '../../success-snackbar/success-snackbar.component';
import { UploadResult } from '../../models/upload-result.model';
import { MultipleFileUpload } from '../../models/multiple-file-upload.model';

@Component({
  selector: 'app-one-qms-file-uploader',
  templateUrl: './one-qms-file-uploader.component.html',
  styleUrls: ['./one-qms-file-uploader.component.scss']
})

export class OneQmsFileUploaderComponent implements OnInit {

  constructor(
    public appService: AppService,
    public message: messageService,
    public dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly oneQmsFileUploadService: OneQmsFileUploadService
  ) { }

  ngOnInit() {
    this.oneQmsFileUploadService.register(this);
  }

  @ViewChild('uploadPopup', { static: true }) uploadPopup: TemplateRef<any>;
  private visibleUploadPopup: MatDialogRef<TemplateRef<any>>;
  @ViewChild('multipleUploadPopup', { static: true }) multipleUploadPopup: TemplateRef<any>;
  private visibleMultipleUploadPopup: MatDialogRef<TemplateRef<any>>;

  private readonly uploadPopupConfig: MatDialogConfig = {
    width: '500px',
    height: 'auto',
    panelClass: 'my-dialog',
    hasBackdrop: true,
    disableClose: true,
    position: { top: '10px' },
  };

  public cancelUploadPopup() {

    if (this.visibleUploadPopup) {
      this.visibleUploadPopup.close();
      this.visibleUploadPopup = undefined as any;
    }

    if (this.visibleMultipleUploadPopup) {
      this.visibleMultipleUploadPopup.close();
      this.visibleMultipleUploadPopup = undefined as any;
    }
  }

  isUploading: boolean = false;
  showProcessing: boolean = false;

  uploadPercent = 0;
  uploadSpeed = 0;
  remainingTime: any = 0;
  startTime: number = 0;
  fileName = "";
  uploadedSize = 0;
  remainingSize = 0;
  totalSize = 0;
  response: any;

  public uploadSingleFile(formData: FormData): Promise<UploadResult> {

    this.isUploading = true;
    this.showProcessing = false;
    this.response = null;
    this.startTime = Date.now();

    return new Promise((resolve, reject) => {

      this.appService.uploadSingleFileToOneQmsServer(formData, true).subscribe({
        next: (event: any) => {

          /* ---------------- Upload Progress ---------------- */
          if (event.type === HttpEventType.UploadProgress && event.total) {

            if (!this.visibleUploadPopup) {
              this.visibleUploadPopup = this.dialog.open(this.uploadPopup, this.uploadPopupConfig);
            }

            const loaded = event.loaded ?? 0;
            const total = event.total ?? 1;
            this.uploadPercent = Math.round((loaded / total) * 100);

            /* convert bytes → MB */
            const loadedMB = loaded / (1024 * 1024);
            const totalMB = total / (1024 * 1024);
            const remainingMB = totalMB - loadedMB;

            this.uploadedSize = +loadedMB.toFixed(2);
            this.remainingSize = +remainingMB.toFixed(2);
            this.totalSize = +totalMB.toFixed(2);

            const elapsed = (Date.now() - this.startTime) / 1000;
            const speedBytes = loaded / (elapsed || 1);

            this.uploadSpeed = +(speedBytes / 1024 / 1024).toFixed(2);

            const remainingSeconds = Math.round(remainingMB / (this.uploadSpeed || 1));
            this.remainingTime = this.formatTime(remainingSeconds);

            if (this.uploadPercent === 100) {
              this.isUploading = false;
              this.showProcessing = true;
            }
          }

          /* ---------------- Final Response ---------------- */
          if (event.type === HttpEventType.Response) {

            this.uploadPercent = 100;
            const response = this.response = event.body;

            if (response?.resultType === 'S') {
              resolve({
                success: true,
                data: response
              });
            } else {
              this.showSnackBar(response?.result || 'Upload failed', 'E');
              resolve({
                success: false,
                data: response
              });
            }

            setTimeout(() => {
              if (this.visibleUploadPopup) {
                this.visibleUploadPopup.close();
                this.visibleUploadPopup = undefined as any;
              }
            }, 1500)

            this.showProcessing = false;
          }
        },

        /* ---------------- Error ---------------- */
        error: (error) => {
          if (this.visibleUploadPopup) {
            this.visibleUploadPopup.close();
            this.visibleUploadPopup = undefined as any;
          }

          this.showSnackBar('Upload error occurred', 'E');
          this.isUploading = false;
          this.showProcessing = false;
          reject(error);
        }
      });
    });
  }

  private formatTime(seconds: number): string {
    if (!seconds || seconds <= 0) return '0 sec';

    const days = Math.floor(seconds / 86400);
    seconds %= 86400;

    const hours = Math.floor(seconds / 3600);
    seconds %= 3600;

    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    let result = '';

    if (days > 0) result += `${days} day `;
    if (hours > 0) result += `${hours} hr `;
    if (minutes > 0) result += `${minutes} min `;
    if (secs > 0) result += `${secs} sec`;

    return result.trim();
  }

  uploadQueue: MultipleFileUpload[] = [];

  MAX_PARALLEL: number;
  currentUploads = 0;

  private multipleUploadCallback?: (res: any) => void;

  uploadMultipleFile(files: File[], callback?: (res: any) => void) {

    this.MAX_PARALLEL = this.message.organizationDetails.maxParallelUploadDocuments ?? 3;

    this.multipleUploadCallback = callback;
    this.uploadQueue = [];

    for (const file of files) {
      this.uploadQueue.push({
        file,
        progress: 0,
        speed: 0,
        remainingTime: '0 sec',
        uploadedSize: 0,
        totalSize: 0,
        status: 'pending'
      });
    }

    if (!this.visibleMultipleUploadPopup) {
      this.visibleMultipleUploadPopup =
        this.dialog.open(this.multipleUploadPopup, this.uploadPopupConfig);
    }

    this.processQueue();
  }

  processQueue() {
    while (this.currentUploads < this.MAX_PARALLEL) {
      const nextFile = this.uploadQueue.find(f => f.status === 'pending');
      if (!nextFile) return;
      this.startUpload(nextFile);
    }
  }

  startUpload(item: MultipleFileUpload) {

    const file = item.file;

    const name = file.name.substring(0, file.name.lastIndexOf('.'));
    const extension = (file.name.split('.').pop() ?? '').toLowerCase();

    const formData = new FormData();
    formData.append('file', file);
    formData.append("pageName", "ORG");
    formData.append("fileName", name);
    formData.append("extension", extension);

    const startTime = Date.now();

    item.status = 'uploading';
    this.currentUploads++;

    const sub = this.appService.uploadSingleFileToOneQmsServer(formData, true).subscribe({
      next: (event: any) => {

        if (event.type === HttpEventType.UploadProgress && event.total) {

          const loaded = event.loaded ?? 0;
          const total = event.total ?? 1;

          item.progress = Math.round((loaded / total) * 100);

          const loadedMB = loaded / (1024 * 1024);
          const totalMB = total / (1024 * 1024);

          item.uploadedSize = +loadedMB.toFixed(2);
          item.totalSize = +totalMB.toFixed(2);

          const elapsed = (Date.now() - startTime) / 1000;
          const speed = loaded / (elapsed || 1);

          item.speed = +(speed / 1024 / 1024).toFixed(2);

          const remainingMB = totalMB - loadedMB;
          const remainingSeconds = Math.round(remainingMB / (item.speed || 1));

          item.remainingTime = this.formatTime(remainingSeconds);
        }

        if (event.type === HttpEventType.Response) {

          const response = event.body;

          item.status = response?.resultType === 'S' ? 'success' : 'error';
          item.message = response?.result || 'Upload failed';

          if (this.multipleUploadCallback) {
            this.multipleUploadCallback({
              file: item.file,
              response: response,
              success: item.status === 'success'
            });
          }

          this.finishUpload();
        }
      },

      error: () => {
        item.status = 'error';
        item.message = 'Upload error';
        this.finishUpload();
      }
    });

    item.subscription = sub;
  }

  finishUpload() {
    this.currentUploads--;

    const allDone = this.uploadQueue.every(f => f.status !== 'uploading' && f.status !== 'pending');

    if (allDone && this.visibleMultipleUploadPopup) {
      setTimeout(() => {
        this.visibleMultipleUploadPopup.close();
        this.visibleMultipleUploadPopup = undefined as any;
      }, 1500);

    }

    this.processQueue();
  }

  cancelUpload(item: MultipleFileUpload) {

    if (item.subscription) {
      item.subscription.unsubscribe();
    }

    item.status = 'cancelled';
    item.message = 'Cancelled';

    this.currentUploads--;
    this.processQueue();
  }

  retryUpload(item: MultipleFileUpload) {

    if (item.subscription) {
      item.subscription.unsubscribe();
    }

    item.progress = 0;
    item.speed = 0;
    item.remainingTime = '0 sec';
    item.uploadedSize = 0;
    item.totalSize = 0;
    item.message = '';
    item.status = 'pending';

    this.processQueue();
  }

  disableCloseButton(array: any) {
    return array.some((x: any) => x.status === 'uploading');
  }

  trackByFile(index: number, item: any) {
    return item.file.name + item.file.lastModified;
  }

  showSnackBar(message: string, type: string) {
    const sb = this.snackBar.openFromComponent(SuccessSnackbarComponent, {
      duration: type === 'S' ? 5000 : undefined,
      data: {
        message,
        type: type === 'S' ? 'Success' : 'Error',
        buttonLabel: 'Ok',
        pageName: this.message.breadcrumbData.thirdLevel ?? this.message.breadcrumbData.secondLevel,
      },
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: type === 'S' ? 'Success' : 'Error',
    });

    sb.onAction().subscribe(() => {
      sb.dismiss();
    });
  }

}
